import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Bell, AlertTriangle, Info } from "lucide-react";

export function NotificationBell() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const timer = useRef(null);

  const load = () => api.get("/notifications").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => {
    load();
    timer.current = setInterval(load, 60000);
    return () => clearInterval(timer.current);
  }, []);

  const unread = items.filter((n) => !n.read).length;

  const onOpenChange = async (o) => {
    setOpen(o);
    if (o && unread > 0) {
      try { await api.post("/notifications/read"); setItems((xs) => xs.map((n) => ({ ...n, read: true }))); } catch {}
    }
  };

  const ago = (ts) => {
    const mins = Math.floor((Date.now() - new Date(ts).getTime()) / 60000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m ago`;
    const h = Math.floor(mins / 60);
    if (h < 24) return `${h}h ago`;
    return `${Math.floor(h / 24)}d ago`;
  };

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <button data-testid="notification-bell" title="Notifications" className="relative text-slate-400 hover:text-slate-700 transition-colors">
          <Bell className="h-5 w-5" />
          {unread > 0 && (
            <span data-testid="notification-badge" className="absolute -top-1.5 -right-1.5 h-4 min-w-[1rem] px-0.5 rounded-full bg-red-600 text-white text-[10px] font-bold grid place-items-center">
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="bg-white w-96 p-0" align="end">
        <div className="px-3 py-2.5 border-b border-slate-100 font-head font-semibold text-sm">Notifications</div>
        <div className="max-h-80 overflow-y-auto scroll-thin">
          {items.length === 0 && <p className="text-sm text-slate-400 text-center py-8">No notifications yet.</p>}
          {items.map((n) => (
            <div key={n.id} data-testid="notification-item" className={`flex items-start gap-2.5 px-3 py-2.5 border-b border-slate-50 last:border-0 ${!n.read ? "bg-amber-50/50" : ""}`}>
              {n.level === "warning" || n.level === "danger"
                ? <AlertTriangle className={`h-4 w-4 mt-0.5 flex-shrink-0 ${n.level === "danger" ? "text-red-500" : "text-amber-500"}`} />
                : <Info className="h-4 w-4 mt-0.5 text-sky-500 flex-shrink-0" />}
              <div className="min-w-0">
                <p className="text-sm text-slate-700 leading-snug">{n.message}</p>
                <p className="text-[10px] text-slate-400 mt-0.5">{ago(n.timestamp)}</p>
              </div>
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
